import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, TextField, Text, BlockStack, Banner, Badge } from '@shopify/polaris';
import { useAuth, useAuthFetch } from './AuthComponent';

import { hasRole } from './constants/roles';
const NOTIFICATION_ROLES = ['admin', 'site_manager', 'customer_service', 'franchise', 'user'];

const filterOrderScoped = (notifications) =>
    (Array.isArray(notifications) ? notifications : [])
        .filter(n => (n.scope === 'order') || !!n.order_id);

/**
 * Order detail page control: Notifications button (with count badge) + modal.
 *
 * The modal lists order-scoped notifications for this order only (whether
 * they were created here or via Settings → Notifications) and lets the
 * operator create, edit, or delete them. Club-scoped notifications that
 * happen to apply to this order are still surfaced in the page banner, but
 * are not managed here — those live in Settings.
 *
 * The local list mirrors the parent's `activeNotifications` prop and is
 * synced on prop changes, plus optimistically updated on each create/edit/
 * delete so the modal reflects the change instantly while `onChange()`
 * re-fetches the order in the background to keep the banner in sync.
 */
export default function OrderNotificationsControl({
    storeKey,
    orderId,
    orderName,
    activeNotifications,
    onChange,
    iconOnly = false,
}) {
    const { user } = useAuth();
    const fetchAuth = useAuthFetch();

    const canManage = user && hasRole(user, NOTIFICATION_ROLES);

    const [open,       setOpen]       = useState(false);
    const [localList,  setLocalList]  = useState(() => filterOrderScoped(activeNotifications));
    const [newMessage, setNewMessage] = useState('');
    const [saving,     setSaving]     = useState(false);
    const [pendingId,  setPendingId]  = useState(null);   // id currently being edited/deleted
    const [editId,     setEditId]     = useState(null);   // notification being edited
    const [editText,   setEditText]   = useState('');
    const [error,      setError]      = useState(null);

    // Re-sync local list whenever the parent's prop changes (e.g., after the
    // background order re-fetch completes, or another tab updates state).
    useEffect(() => {
        setLocalList(filterOrderScoped(activeNotifications));
    }, [activeNotifications]);

    const close = useCallback(() => {
        setOpen(false);
        setNewMessage('');
        setEditId(null);
        setEditText('');
        setError(null);
    }, []);

    const handleCreate = useCallback(async () => {
        const msg = (newMessage || '').trim();
        if (!msg) return;
        setSaving(true);
        setError(null);
        try {
            const res = await fetchAuth('/api/settings/notifications', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    store_key: storeKey,
                    order_id: String(orderId),
                    order_name: orderName,
                    message: msg,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
            // Optimistic merge — replace existing row with same id (upsert) or append.
            setLocalList(prev => {
                const without = prev.filter(n => n.id !== data.id);
                return [...without, data];
            });
            setNewMessage('');
            if (onChange) { try { await onChange(); } catch { /* parent re-fetch best-effort */ } }
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            setSaving(false);
        }
    }, [newMessage, fetchAuth, storeKey, orderId, orderName, onChange]);

    const startEdit = useCallback((n) => {
        setEditId(n.id);
        setEditText(n.message || '');
        setError(null);
    }, []);

    const cancelEdit = useCallback(() => {
        setEditId(null);
        setEditText('');
    }, []);

    const handleEditSave = useCallback(async () => {
        const msg = (editText || '').trim();
        if (!msg || editId == null) return;
        setPendingId(editId);
        setError(null);
        try {
            const res = await fetchAuth('/api/settings/notifications', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: editId,
                    store_key: storeKey,
                    message: msg,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
            setLocalList(prev => prev.map(n => n.id === editId ? { ...n, ...data } : n));
            setEditId(null);
            setEditText('');
            if (onChange) { try { await onChange(); } catch { /* best-effort */ } }
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            setPendingId(null);
        }
    }, [editId, editText, fetchAuth, storeKey, orderId, orderName, onChange]);

    const handleDelete = useCallback(async (id) => {
        setPendingId(id);
        setError(null);
        try {
            const res = await fetchAuth(`/api/settings/notifications/${id}`, { method: 'DELETE' });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            setLocalList(prev => prev.filter(n => n.id !== id));
            if (editId === id) { setEditId(null); setEditText(''); }
            if (onChange) { try { await onChange(); } catch { /* best-effort */ } }
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            setPendingId(null);
        }
    }, [editId, fetchAuth, onChange]);

    if (!canManage) return null;

    const newCharsLeft = 300 - newMessage.length;
    const editCharsLeft = 300 - editText.length;
    const hasAny = localList.length > 0;

    return (
        <>
            {/* Native button styled with `od-action-btn` so its size matches the
              * other order-detail action buttons (Refund / Print Order). Polaris
              * Button is taller and visually heavier — using it here made the
              * action bar read as mismatched. */}
            <button
                type="button"
                className={iconOnly ? 'odv3-btn odv3-btn--ico' : 'od-action-btn'}
                onClick={() => setOpen(true)}
                title={iconOnly ? 'Notifications' : undefined}
                aria-label={iconOnly ? 'Notifications' : undefined}
            >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 3a5 5 0 00-5 5v3.5L3.5 14h13L15 11.5V8a5 5 0 00-5-5zM8 16a2 2 0 004 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {!iconOnly && 'Notifications'}
                {hasAny && (iconOnly
                    ? <span className="odv3-ico-badge">{localList.length}</span>
                    : <Badge tone="warning">{String(localList.length)}</Badge>)}
            </button>

            {open && (
                <Modal
                    open={open}
                    onClose={close}
                    title={`Notifications for ${orderName || `Order #${orderId}`}`}
                    primaryAction={{ content: 'Done', onAction: close }}
                >
                    <Modal.Section>
                        <BlockStack gap="400">
                            {error && (
                                <Banner tone="critical" onDismiss={() => setError(null)}>
                                    {error}
                                </Banner>
                            )}

                            {hasAny && (
                                <BlockStack gap="200">
                                    <Text variant="headingSm" as="h3">Active order notifications</Text>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {localList.map(n => {
                                            const isEditing = editId === n.id;
                                            const isBusy    = pendingId === n.id;
                                            return (
                                                <div
                                                    key={n.id}
                                                    style={{
                                                        border: '1px solid #f5a5a5',
                                                        borderRadius: 8,
                                                        padding: 12,
                                                        background: '#fde8e8',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 8,
                                                    }}
                                                >
                                                    {isEditing ? (
                                                        <>
                                                            <TextField
                                                                label={`Message (${editCharsLeft} chars left)`}
                                                                labelHidden
                                                                value={editText}
                                                                onChange={v => setEditText(v.slice(0, 300))}
                                                                multiline={3}
                                                                maxLength={300}
                                                                autoComplete="off"
                                                                error={editText.length > 300 ? 'Too long' : undefined}
                                                            />
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                                                                <Button onClick={cancelEdit} disabled={isBusy}>Cancel</Button>
                                                                <Button
                                                                    variant="primary"
                                                                    loading={isBusy}
                                                                    disabled={isBusy || !editText.trim()}
                                                                    onClick={handleEditSave}
                                                                >
                                                                    Save
                                                                </Button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                                            <div style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                {n.message}
                                                            </div>
                                                            <div style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
                                                                <Button size="slim" onClick={() => startEdit(n)} disabled={isBusy}>Edit</Button>
                                                                <Button
                                                                    tone="critical"
                                                                    variant="primary"
                                                                    size="slim"
                                                                    loading={isBusy}
                                                                    onClick={() => handleDelete(n.id)}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </BlockStack>
                            )}

                            <BlockStack gap="200">
                                <Text variant="headingSm" as="h3">
                                    {hasAny ? '+ New notification for this order' : 'New notification for this order'}
                                </Text>
                                <TextField
                                    label={`Message (${newCharsLeft} chars left)`}
                                    value={newMessage}
                                    onChange={v => setNewMessage(v.slice(0, 300))}
                                    multiline={3}
                                    maxLength={300}
                                    autoComplete="off"
                                    error={newMessage.length > 300 ? 'Too long' : undefined}
                                />
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="primary"
                                        loading={saving}
                                        disabled={saving || !newMessage.trim()}
                                        onClick={handleCreate}
                                    >
                                        Create
                                    </Button>
                                </div>
                            </BlockStack>
                        </BlockStack>
                    </Modal.Section>
                </Modal>
            )}
        </>
    );
}
